import ButtonV2, { getButtonClasses } from "components/elements/buttonV2"
import YoutubeVideo from "components/elements/youtubeVideo"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { useAllPodcastEpisodes } from "services/graphQl/queries/allPodcastEpisodes"
import TOPICS from "utils/podcastTopics.json"
import appleIcon from "../../components/sections/sectionDarkmodePodcastHighlightV2/apple.svg"
import spotifyIcon from "../../components/sections/sectionDarkmodePodcastHighlightV2/spotify.svg"
import youtubeIcon from "../../components/sections/sectionDarkmodePodcastHighlightV2/youtube.png"
import "./podcast-library.scss"

// How many episodes show before "See more episodes", and how many each click
// adds. Phones (one episode per row) start shorter.
const PAGE_SIZE = 12
const PHONE_FIRST = 3
const PHONE_MORE = 6

export const ListenLinks = ({
  applePodcastLink,
  spotifyLink,
  title,
  youtubeLink,
}) => {
  const links = [
    ["Apple Podcasts", applePodcastLink, appleIcon],
    ["Spotify", spotifyLink, spotifyIcon],
    ["YouTube", youtubeLink, youtubeIcon],
  ].filter(([, href]) => href)
  if (!links.length) return null
  return (
    <p className="podcast-library--listen">
      {links.map(([name, href, icon]) => (
        <a
          aria-label={`Listen to ${title} on ${name} (opens in a new tab)`}
          href={href}
          key={name}
          rel="noopener noreferrer"
          target="_blank"
        >
          <img alt="" height="20" src={icon} width="20" />
        </a>
      ))}
    </p>
  )
}

ListenLinks.propTypes = {
  applePodcastLink: PropTypes.string,
  spotifyLink: PropTypes.string,
  title: PropTypes.string,
  youtubeLink: PropTypes.string,
}

export const details = episode =>
  [
    episode.releaseDate,
    episode.durationMinutes ? `${episode.durationMinutes} min` : null,
    episode.episode ? `Episode ${episode.episode}` : null,
  ]
    .filter(Boolean)
    .join(" | ")

export const TopicTags = ({ topics }) =>
  topics?.length ? (
    <ul className="podcast-library--tags">
      {topics.map(topic => (
        <li key={topic}>{topic}</li>
      ))}
    </ul>
  ) : null

TopicTags.propTypes = {
  topics: PropTypes.arrayOf(PropTypes.string),
}

// The episode's own page (see gatsby/podcastEpisodePages.js).
export const episodePath = episode => `/podcast/${episode.slug}`

export const EpisodeCard = ({ episode }) => (
  <article className="podcast-library--card">
    {episode.youtubeLink && (
      <YoutubeVideo title={episode.title} url={episode.youtubeLink} />
    )}
    <h3 className="podcast-library--card-title">
      <a href={episodePath(episode)}>{episode.title}</a>
    </h3>
    <p className="podcast-library--details">{details(episode)}</p>
    <TopicTags topics={episode.topics} />
    <ListenLinks {...episode} />
  </article>
)

EpisodeCard.propTypes = {
  episode: PropTypes.object.isRequired,
}

/**
 * PodcastLibrary - every Magnificent Minds episode (read from the podcast's
 * feed at build time; see gatsby/podcastEpisodes.js) with a search box, topic
 * filters (utils/podcastTopics.json), the latest episode highlighted, and the
 * rest in a grid. Shown by a Template Section titled "Podcast Episode Library".
 */
const PodcastLibrary = () => {
  const episodes = useAllPodcastEpisodes()
  const [query, setQuery] = useState("")
  const [topic, setTopic] = useState(null)
  // Episodes shown beyond the first page.
  const [shown, setShown] = useState(0)
  const [isPhone, setIsPhone] = useState(false)

  // Checked after the page loads, so the built page and the browser agree.
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 767px)")
    const update = () => setIsPhone(phone.matches)
    update()
    phone.addEventListener("change", update)
    return () => phone.removeEventListener("change", update)
  }, [])

  const firstPage = isPhone ? PHONE_FIRST : PAGE_SIZE
  const morePerClick = isPhone ? PHONE_MORE : PAGE_SIZE
  const visible = firstPage + shown

  const counts = useMemo(() => {
    const result = {}
    episodes.forEach(episode =>
      (episode.topics || []).forEach(name => {
        result[name] = (result[name] || 0) + 1
      })
    )
    return result
  }, [episodes])

  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  const filtering = words.length > 0 || topic
  const matches = episodes.filter(
    episode =>
      (!topic || (episode.topics || []).includes(topic)) &&
      words.every(word =>
        [episode.title, episode.searchText, ...(episode.topics || [])]
          .join(" ")
          .toLowerCase()
          .includes(word)
      )
  )
  const [latest, ...rest] = episodes
  const list = filtering ? matches : rest

  const chooseTopic = name => {
    setTopic(topic === name ? null : name)
    setShown(0)
  }
  const clear = () => {
    setQuery("")
    setTopic(null)
    setShown(0)
  }

  if (!episodes.length) return null

  return (
    <section className="podcast-library">
      <div className="container-v2">
        <div className="podcast-library--find">
          <h2 className="podcast-library--heading">
            Looking for something specific?
          </h2>
          <form
            className="podcast-library--search"
            onSubmit={event => event.preventDefault()}
            role="search"
          >
            <label
              className="podcast-library--search-label"
              htmlFor="podcast-search"
            >
              Search episodes
            </label>
            <svg
              aria-hidden="true"
              fill="none"
              height="20"
              viewBox="0 0 24 24"
              width="20"
            >
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="m20 20-3.5-3.5"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
              />
            </svg>
            <input
              id="podcast-search"
              onChange={event => {
                setQuery(event.target.value)
                setShown(0)
              }}
              placeholder="Search episodes, topics or guests"
              type="search"
              value={query}
            />
          </form>
          <ul aria-label="Topics" className="podcast-library--topics">
            {TOPICS.filter(({ name }) => counts[name]).map(({ icon, name }) => (
              <li key={name}>
                <button
                  aria-pressed={topic === name}
                  className="podcast-library--topic"
                  onClick={() => chooseTopic(name)}
                  type="button"
                >
                  <img alt="" src={`/images/podcast-topics/${icon}`} />
                  <span className="podcast-library--topic-name">{name}</span>
                  <span className="podcast-library--topic-count">
                    {counts[name]} {counts[name] === 1 ? "episode" : "episodes"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {!filtering && latest && (
        <div className="podcast-library--latest">
          <div className="container-v2">
            <h2 className="podcast-library--heading">Latest episode</h2>
            <article className="podcast-library--latest-card">
              {latest.youtubeLink && (
                <YoutubeVideo title={latest.title} url={latest.youtubeLink} />
              )}
              <div>
                <h3 className="podcast-library--latest-title">
                  <a href={episodePath(latest)}>{latest.title}</a>
                </h3>
                <p className="podcast-library--details">{details(latest)}</p>
                <TopicTags topics={latest.topics} />
                {latest.summary && (
                  <p className="podcast-library--summary">{latest.summary}</p>
                )}
                <ListenLinks {...latest} />
              </div>
            </article>
          </div>
        </div>
      )}

      <div className="container-v2">
        <div className="podcast-library--all">
          <div className="podcast-library--all-header">
            <h2 className="podcast-library--heading">
              {filtering ? topic || "Search results" : "All episodes"}
            </h2>
            <p aria-live="polite" className="podcast-library--count">
              {filtering
                ? `${matches.length} ${
                    matches.length === 1 ? "episode" : "episodes"
                  }${words.length ? ` matching "${query.trim()}"` : ""}`
                : `${episodes.length} episodes`}
              {filtering && (
                <button
                  className="podcast-library--clear"
                  onClick={clear}
                  type="button"
                >
                  Clear
                </button>
              )}
            </p>
          </div>
          {list.length > 0 ? (
            <div className="podcast-library--grid">
              {list.slice(0, visible).map(episode => (
                <EpisodeCard episode={episode} key={episode.id} />
              ))}
            </div>
          ) : (
            <p className="podcast-library--empty">
              No episodes match. Try another word, or clear the filters to see
              every episode.
            </p>
          )}
          {list.length > visible && (
            <p className="podcast-library--more">
              <ButtonV2
                className={getButtonClasses("Outline Blue", "small")}
                onClick={() => setShown(shown + morePerClick)}
                text="See more episodes"
              />
            </p>
          )}
        </div>
      </div>
    </section>
  )
}

export default PodcastLibrary
